import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { aiSignalsAPI, AISignal } from '@/lib/ai-signals-api';
import { PortfolioOptimizerApiClient, OptimizationResult } from '@/lib/portfolio-optimizer-api';

interface Holding {
  mint: string;
  uiAmount: number;
  valueUsd: number;
}

interface RebalancingTrigger {
  asset: string;
  current_weight: number;
  optimal_weight: number;
  deviation: number;
  trigger_threshold: number;
  should_rebalance: boolean;
}

interface PortfolioConstraints {
  max_allocation_per_asset?: number;
  min_allocation_per_asset?: number;
  max_risk_score?: number;
  risk_tolerance?: number;
  total_portfolio_value?: number;
}

const portfolioOptimizerApi = new PortfolioOptimizerApiClient();

/**
 * Maps numeric risk tolerance (0-1) to string enum for backend compatibility
 */
const mapRiskToleranceToString = (numericRisk: number): "CONSERVATIVE" | "MODERATE" | "AGGRESSIVE" => {
  if (numericRisk <= 0.33) return "CONSERVATIVE";
  if (numericRisk <= 0.66) return "MODERATE";
  return "AGGRESSIVE";
};

/**
 * Custom hook for portfolio optimization functionality
 *
 * Handles the complete optimization workflow:
 * - Prepares optimization data (returns, covariance matrix, constraints)
 * - Fetches AI signals for Black-Litterman model
 * - Calls optimization API and polls for results
 * - Calculates position sizes and rebalancing triggers
 * - Manages loading states and error handling
 *
 * @param strategy - Optimization strategy ('MPT', 'BLACK_LITTERMAN', 'RISK_PARITY')
 * @param holdings - Array of portfolio holdings with mint, amount, and USD value
 * @returns Object containing optimization results, loading states, and control functions
 */
export function usePortfolioOptimizer(
  strategy: string,
  holdings: Holding[]
) {
  const queryClient = useQueryClient();
  const [optimization, setOptimization] = useState<OptimizationResult | null>(null);
  const [positionSizes, setPositionSizes] = useState<Record<string, number>>({});
  const [rebalancingTriggers, setRebalancingTriggers] = useState<RebalancingTrigger[]>([]);
  const [coinIdMap, setCoinIdMap] = useState<Record<string, string>>({});
  const [signalInfluence, setSignalInfluence] = useState<Record<string, { signal: AISignal; weightChange: number }>>({});

  /**
   * Prepares all necessary data for portfolio optimization
   *
   * Process:
   * 1. Maps mint addresses to coin IDs for database queries
   * 2. Fetches AI signals for expected returns (especially for Black-Litterman)
   * 3. Calculates expected returns from signals with confidence weighting
   * 4. Computes covariance matrix from historical price data
   * 5. Sets portfolio constraints based on risk tolerance
   *
   * @returns Promise resolving to optimization data object
   */
  const prepareOptimizationData = useCallback(async () => {
    const assets = holdings.map(h => h.mint);
    const portfolioValue = holdings.reduce((sum, h) => sum + h.valueUsd, 0);

    // Fetch coin IDs for each mint
    const coinIdMap: Record<string, string> = {};
    for (const mint of assets) {
      try {
        const { data: coin } = await supabase
          .from('coins')
          .select('id')
          .eq('mint_address', mint)
          .single();

        if (coin) {
          coinIdMap[mint] = coin.id;
        }
      } catch (error) {
        console.warn(`Failed to fetch coin ID for mint ${mint}:`, error);
      }
    }

    setCoinIdMap(coinIdMap);

    // Get expected returns from AI signals (especially for Black-Litterman)
    const signals: AISignal[] = [];
    for (const asset of assets) {
      try {
        // Get signals for this asset (assuming ticker matches mint for simplicity)
        const assetSignals = await aiSignalsAPI.getSignals({ symbol: asset }, 1, 1);
        if (assetSignals.signals.length > 0) {
          signals.push(assetSignals.signals[0]);
        }
      } catch (error) {
        console.warn(`Failed to get signals for ${asset}:`, error);
      }
    }

    // Calculate expected returns from signals
    const expected_returns: Record<string, number> = {};
    const signalInfluenceData: Record<string, { signal: AISignal; weightChange: number }> = {};

    for (const signal of signals) {
      const expectedReturn = signal.price_target && signal.current_price
        ? (signal.price_target - signal.current_price) / signal.current_price
        : signal.confidence_score * 0.1; // Fallback based on confidence

      expected_returns[signal.symbol] = expectedReturn;

      // For Black-Litterman, calculate weight influence
      if (strategy === 'BLACK_LITTERMAN') {
        // Calculate equilibrium weight (market cap weighted)
        const equilibriumWeight = holdings.find(h => h.mint === signal.symbol)?.valueUsd / portfolioValue || 0;
        // Optimal weight from Black-Litterman model (simplified)
        const optimalWeight = Math.max(0.01, Math.min(0.3, equilibriumWeight * (1 + expectedReturn * signal.confidence_score)));
        const weightChange = optimalWeight - equilibriumWeight;

        signalInfluenceData[signal.symbol] = {
          signal,
          weightChange
        };
      }
    }

    // Fill missing returns with default
    for (const asset of assets) {
      if (!expected_returns[asset]) {
        expected_returns[asset] = 0.05; // 5% default expected return
      }
    }

    // Compute covariance matrix from historical prices
    // This is crucial for MPT and Black-Litterman optimization as it represents asset relationships
    const covarianceMatrix: Record<string, Record<string, number>> = {};
    const priceHistories: Record<string, number[]> = {};

    // Fetch recent price history for volatility and correlation calculations
    for (const asset of assets) {
      try {
        const { data: snapshots } = await supabase
          .from('market_cap_snapshots')
          .select('price')
          .eq('coin_id', asset) // Note: This assumes coin_id matches mint, may need adjustment
          .order('timestamp', { ascending: true })
          .limit(30); // Use last 30 data points for recent volatility estimate

        priceHistories[asset] = snapshots?.map(s => s.price) || [0.0001]; // Fallback for missing data
      } catch (error) {
        console.warn(`Failed to fetch price history for ${asset}:`, error);
        priceHistories[asset] = [0.0001]; // Minimal fallback to prevent division by zero
      }
    }

    // Calculate covariance matrix using statistical formulas
    // Covariance measures how assets move together (positive = same direction, negative = opposite)
    for (const asset1 of assets) {
      covarianceMatrix[asset1] = {};
      for (const asset2 of assets) {
        if (asset1 === asset2) {
          // Calculate variance (covariance with itself)
          const prices = priceHistories[asset1];
          const mean = prices.reduce((sum, p) => sum + p, 0) / prices.length;
          const variance = prices.reduce((sum, p) => sum + Math.pow(p - mean, 2), 0) / prices.length;
          covarianceMatrix[asset1][asset2] = variance;
        } else {
          // Calculate covariance between different assets
          const prices1 = priceHistories[asset1];
          const prices2 = priceHistories[asset2];
          const mean1 = prices1.reduce((sum, p) => sum + p, 0) / prices1.length;
          const mean2 = prices2.reduce((sum, p) => sum + p, 0) / prices2.length;

          // Covariance formula: Cov(X,Y) = Σ((Xi-meanX) * (Yi-meanY)) / n
          const covariance = prices1.reduce((sum, p, i) =>
            sum + (p - mean1) * ((prices2[i] || mean2) - mean2), 0
          ) / prices1.length;
          covarianceMatrix[asset1][asset2] = covariance;
        }
      }
    }

    // Set constraints (simplified - could be from user preferences)
    const constraints: PortfolioConstraints = {
      max_allocation_per_asset: 0.3, // 30% max per asset
      min_allocation_per_asset: 0.01, // 1% min per asset
      risk_tolerance: 0.5, // Medium risk tolerance
      total_portfolio_value: portfolioValue,
    };

    return {
      assets,
      expected_returns,
      covariance_matrix: covarianceMatrix,
      constraints,
      portfolio_value: portfolioValue,
      signalInfluenceData,
    };
  }, [holdings]);

  // Optimization mutation
  const optimizationMutation = useMutation({
    mutationFn: async () => {
      const data = await prepareOptimizationData();

      // Call optimization API
      const result = await portfolioOptimizerApi.optimizePortfolio(strategy, {
        expected_returns: data.expected_returns,
        covariance_matrix: data.covariance_matrix,
        constraints: data.constraints,
      });

      return { result, data };
    },
    onSuccess: async ({ result, data }) => {
      setOptimization(result);
      setSignalInfluence(data.signalInfluenceData);

      // Fetch position sizes
      try {
        const numericRiskTolerance = data.constraints.risk_tolerance || 0.5;
        const stringRiskTolerance = mapRiskToleranceToString(numericRiskTolerance);
        const positionResponse = await portfolioOptimizerApi.calculatePositionSizes(
          result.optimal_weights,
          data.portfolio_value,
          stringRiskTolerance
        );
        setPositionSizes(positionResponse.position_sizes);
      } catch (error) {
        console.error('Failed to fetch position sizes:', error);
      }

      // Fetch rebalancing triggers
      try {
        // Calculate current weights from holdings
        const currentWeights: Record<string, number> = {};
        for (const holding of holdings) {
          currentWeights[holding.mint] = holding.valueUsd / data.portfolio_value;
        }

        const triggersResponse = await portfolioOptimizerApi.getRebalancingTriggers(
          currentWeights,
          result.optimal_weights
        );
        setRebalancingTriggers(triggersResponse.triggers);
      } catch (error) {
        console.error('Failed to fetch rebalancing triggers:', error);
      }

      // Cache the results
      queryClient.setQueryData(
        ['portfolio-optimization', strategy, holdings],
        { optimization: result, positionSizes, rebalancingTriggers, signalInfluence: data.signalInfluenceData }
      );
    },
    onError: (error) => {
      console.error('Portfolio optimization failed:', error);
    },
  });

  // Manual optimize function
  const optimize = useCallback(async () => {
    await optimizationMutation.mutateAsync();
  }, [optimizationMutation]);

  return {
    optimization,
    positionSizes,
    rebalancingTriggers,
    coinIdMap,
    signalInfluence,
    isOptimizing: optimizationMutation.isPending,
    error: optimizationMutation.error as Error | null,
    optimize,
  };
}